import {Stack, TextField, Typography} from "@mui/material";
import type {WordResolver} from "../../../../../../sdo/Resolver.ts";
import {OperatorToggle, F, Sel, Sw, SummaryPill, SummaryPillRow, MatchModeRow, rowSx, fieldSx, labelSx, set, setNum, type ResolverEditorProps} from "./ResolverUtils.tsx";

function WordEditor({draft, editing, onChange}: ResolverEditorProps & { draft: WordResolver }) {
    if (!editing) {
        return (
            <Stack direction="column" spacing={0.5} sx={{alignItems: "center", width: "100%"}}>
                <SummaryPillRow>
                    <SummaryPill label="Operator" value={draft.operator}/>
                    <SummaryPill label="Value" value={draft.value}/>
                </SummaryPillRow>
                <SummaryPillRow>
                    <SummaryPill label="Language" value={draft.inputLang}/>
                    <SummaryPill label="Alpha Match" value={draft.alphaMatch ? "Yes" : "No"}/>
                    <SummaryPill label="Char Match" value={draft.charMatch ? "Yes" : "No"}/>
                </SummaryPillRow>
                <SummaryPillRow>
                    <SummaryPill label="Translate" value={draft.translate ? "Yes" : "No"}/>
                    <SummaryPill label="Misspelled" value={draft.misspelled ? "Yes" : "No"}/>
                    <SummaryPill label="Acronym" value={draft.acronym ? "Yes" : "No"}/>
                </SummaryPillRow>
                <SummaryPillRow>
                    <SummaryPill label="Abbreviation" value={draft.abbreviation ? "Yes" : "No"}/>
                    {draft.operator === "CONTAINS" && (
                        <>
                            <SummaryPill label="Contains" value={draft.containsType}/>
                            <SummaryPill label="Min" value={draft.containsMin}/>
                        </>
                    )}
                </SummaryPillRow>
                {draft.operator === "CONTAINS" && (
                    <SummaryPillRow>
                        <SummaryPill label="Distance" value={draft.containsDistance}/>
                        <SummaryPill label="Distance Max" value={draft.containsDistanceMax}/>
                    </SummaryPillRow>
                )}
            </Stack>
        );
    }

    const upd = (key: string, value: unknown) => set(draft, key, value, onChange);
    const updNum = (key: string, raw: string) => setNum(draft, key, raw, onChange);

    return (
        <Stack direction="column" spacing={1} alignItems="center">
            {/* Row 1: Match mode + Alpha/Char switches */}
            <MatchModeRow draft={draft} editing={editing} onChange={onChange}/>
            <Stack direction="row" sx={rowSx}>
                <Sw checked={draft.alphaMatch} label="Alpha Match" onChange={(v) => upd("alphaMatch", v)}/>
                <Sw checked={draft.charMatch} label="Character Match" onChange={(v) => upd("charMatch", v)}/>
            </Stack>

            {/* Row 2: Operator + Input + Language */}
            <Stack direction="row" sx={rowSx}>
                <OperatorToggle
                    value={draft.operator}
                    options={[
                        {label: "Word", value: "WORD"},
                        {label: "Phrase", value: "PHRASE"},
                        {label: "Contains", value: "CONTAINS"},
                    ]}
                    onChange={(v) => upd("operator", v)}
                />
                <F value={draft.value} placeholder="Input Text" onChange={(v) => upd("value", v)} width="180px"/>
                <Sel value={draft.inputLang} label="Language" onChange={(v) => upd("inputLang", v)} minWidth="120px" options={[
                    {value: "ENGLISH", label: "English"},
                    {value: "SPANISH", label: "Spanish"},
                    {value: "FRENCH", label: "French"},
                    {value: "GERMAN", label: "German"},
                    {value: "ARABIC", label: "Arabic"},
                    {value: "CHINESE", label: "Chinese"},
                    {value: "RUSSIAN", label: "Russian"},
                ]}/>
            </Stack>

            {/* Row 3: Switches */}
            <Stack direction="row" sx={rowSx}>
                <Sw checked={draft.translate} label="Translate" onChange={(v) => upd("translate", v)}/>
                <Sw checked={draft.misspelled} label="Misspelled" onChange={(v) => upd("misspelled", v)}/>
                <Sw checked={draft.acronym} label="Acronym" onChange={(v) => upd("acronym", v)}/>
            </Stack>
            <Stack direction="row" sx={rowSx}>
                <Sw checked={draft.abbreviation} label="Abbreviation" onChange={(v) => upd("abbreviation", v)}/>
            </Stack>

            {/* Row 4: Contains options — only visible when operator is CONTAINS */}
            {draft.operator === "CONTAINS" && (
                <>
                    <Stack direction="row" sx={rowSx}>
                        <Typography sx={labelSx}>Contains</Typography>
                        <OperatorToggle
                            value={draft.containsType}
                            options={[
                                {label: "Any", value: "ANY"},
                                {label: "All", value: "ALL"},
                                {label: "Minimum", value: "MIN"},
                            ]}
                            onChange={(v) => upd("containsType", v)}
                        />
                        {draft.containsType === "MIN" && (
                            <Stack direction="row" sx={{alignItems: "center", gap: "4px"}}>
                                <Typography sx={labelSx}>minimum of</Typography>
                                <TextField size="small" type="number" value={draft.containsMin}
                                           onChange={(e) => updNum("containsMin", e.target.value)}
                                           sx={{...fieldSx, width: "56px"}}/>
                                <Typography sx={labelSx}>condition(s)</Typography>
                            </Stack>
                        )}
                    </Stack>
                    <Stack direction="row" sx={rowSx}>
                        <Typography sx={labelSx}>Distance:</Typography>
                        <OperatorToggle
                            value={draft.containsDistance}
                            options={[
                                {label: "Any", value: "ANY"},
                                {label: "Maximum", value: "MAX"},
                            ]}
                            onChange={(v) => upd("containsDistance", v)}
                        />
                        {draft.containsDistance === "MAX" && (
                            <Stack direction="row" sx={{alignItems: "center", gap: "4px"}}>
                                <Typography sx={labelSx}>maximum of</Typography>
                                <TextField size="small" type="number" value={draft.containsDistanceMax}
                                           onChange={(e) => updNum("containsDistanceMax", e.target.value)}
                                           sx={{...fieldSx, width: "56px"}}/>
                                <Typography sx={labelSx}>words between</Typography>
                            </Stack>
                        )}
                    </Stack>
                </>
            )}
        </Stack>
    );
}

export default WordEditor;
